import {
    BaseSyntheticEvent,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useWindowSize } from '@uidotdev/usehooks';
import { Event } from 'event-source-polyfill';

const defaultWidth = 1280;

export const useMobileViewDetection = () => {
    const { width } = useWindowSize();

    const isMobile = (width ?? defaultWidth) < 467;

    return useMemo(
        () => ({
            isMobile,
        }),
        [isMobile],
    );
};

export const useOnScrollEndListener = (
    bottomOffset: number = 0,
    scrollContainerId: string = 'root',
) => {
    const rootBox = document.getElementById(scrollContainerId);
    const [bottomReached, setBottomReached] = useState(false);

    useEffect(() => {
        if (rootBox) {
            let lastScrollTop = null;
            const onScroll = (e: Event) => {
                lastScrollTop = e.target.scrollTop;
                const bottom =
                    e.target.scrollHeight -
                        (e.target.scrollTop + bottomOffset) <=
                    e.target.clientHeight;
                if (bottom && !bottomReached) {
                    setBottomReached(true);
                }
                if (lastScrollTop && lastScrollTop > e.target.scrollTop) {
                    setBottomReached(false);
                }
            };

            rootBox.addEventListener('scroll', onScroll);
            return () => {
                rootBox.removeEventListener('scroll', onScroll);
            };
        }
    }, [rootBox, bottomReached, bottomOffset]);

    return useMemo(() => {
        const scrollToTop = () => {
            if (rootBox) {
                rootBox.scrollTo({ top: 0, behavior: 'smooth' });
            }
        };

        const reset = () => {
            setBottomReached(false);
        };

        return {
            scrollToTop,
            bottomReached,
            reset,
        };
    }, [rootBox, bottomReached]);
};

export const useOnScrollListener = () => {
    const [bottomReached, setBottomReached] = useState(false);

    return useMemo(() => {
        const connectHandler = (e: BaseSyntheticEvent) => {
            const bottom =
              e.target.scrollHeight - (e.target.scrollTop + 50) <=
              e.target.clientHeight;
            if (bottom && !bottomReached) {
                setBottomReached(true);
            }
        };

        const reset = () => {
            setBottomReached(false);
        };

        return {
            bottomReached,
            connectHandler,
            reset,
        };
    }, [bottomReached]);
};
